// 
// ─── IMPORTING NECESSARY ELEMENTS ───────────────────────────────────────
//
import { React, useState, useEffect } from 'react';
import Cookies from 'universal-cookie';

// ─── MATERIAL UI ────────────────────────────
// COMPONENTS
import {
  Typography
} from "@mui/material";
//
// ─── UTILS ────────────────────────────
//
import Api from '../../../contexts/Api';
//
// ─── STYLE ────────────────────────────
//
import { createUseStyles } from "react-jss";

// 
// ─── IMPORTING COMPONENTS ───────────────────────────────────────
//
import AnalyticsTable from '../../../components/Admin/Analytics/AnalyticsTable';
import DoughnutAdmin from '../../../components/Admin/Analytics/DoughnutAdmin';
import BarAdmin from '../../../components/Admin/Analytics/BarAdmin';
import MarketingAnalyticsTable from '../../../components/Admin/Analytics/MarketingAnalyticsTable';
import ExportServer from '../../../components/Admin/ExportServer';
import UsersBar from '../../../components/Admin/Analytics/UsersBar';
import { Link } from 'react-router-dom';
import { timeStamp } from '../../../contexts/Utils';
import BlogTable from '../../../components/Admin/Analytics/BlogTable';

// 
// ─── COMPONENT DECLARATION ───────────────────────────────────────
//
const ListAnalytics = () => {
  //
  // ─── DECLARATION DE MON STATE ───────────────────────────────────────
  //
  // ANALYTICS USER REGISTERED
  const [analyticsUsers, setAnalyticsUsers] = useState([]);
  // ANALYTICS POINTS USERS
  const [userPoints, setUserPoints] = useState([]);
  // ANALYTICS GAINS LOYALTY UTILISES
  const [gainsPoints, setGainsPoints] = useState([]);
  // ANALYTICS GAINS COMPTA
  const [gainsCompta, setGainsCompta] = useState([]);
  // BRANDS
  const [brands, setBrands] = useState([]);
  // TRACKING ACTU OFFER
  const [trackingActuOffer, setTrackingActuOffer] = useState([]);
  // OFFERS
  const [offers, setOffers] = useState([]);
  // ACTUALITIES
  const [actualities, setActualities] = useState([]);
  // LOADING
  const [loading, setLoading] = useState(true);

  // Cookies
  const cookies = new Cookies();
  const userData = cookies.get(process.env.REACT_APP_USER_COOKIE);

  //
  // ─── INITIALISATION DES STYLES ───────────────────────────────────────
  //
  const useStyle = createUseStyles({
    page: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      minWidth: 0,
      paddingTop: 45,

      '& h1': {
        marginBottom: 0,
        fontSize: 'clamp(1.8rem, 4vw, 3rem)',
      },
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#E17F51',
      margin: '16px 0 -8px',
    },
    // Grille generique : 2 colonnes sur grand ecran, 1 colonne en dessous de 1200px
    grid2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 24,
      '@media (max-width: 1200px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
      },
    },
    // Ligne du haut : KPI inscrits + enseignes
    topRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)',
      gap: 24,
      '@media (max-width: 900px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
      },
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      minWidth: 0,
      padding: 24,
      background: '#FFFFFF',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06), 0px 8px 32px rgba(0, 0, 0, 0.04)',
      borderRadius: 12,

      '@media (max-width: 600px)': {
        padding: 16,
      },

      '& .text': {
        fontSize: 14,
        fontWeight: 400,
        opacity: 0.6,
      },

      '& .MuiInputBase-root': {
        padding: 0,
      },

      '& fieldset': {
        border: '1px solid #999999',
        borderRadius: 3,
      },
    },
    cardHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    cardTitle: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
      fontSize: '1.25rem !important',
      fontWeight: '700 !important',
      margin: '0 !important',
    },
    kpi: {
      justifyContent: 'center',
      '& .number': {
        fontSize: 'clamp(3rem, 6vw, 4.5rem)',
        lineHeight: 1,
        color: '#E17F51',
        fontWeight: 900,
      },
    },
    brandContainer: {
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
      gap: 10,
    },
    brandLink: {
      display: 'flex',
      aspectRatio: '1 / 1',
      padding: 12,
      background: '#F3F4FA',
      borderRadius: 8,
      transition: 'transform .15s, box-shadow .15s',

      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      },

      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
    },
    doughnuts: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: 24,
      alignItems: 'start',
    },
    doughnutCol: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      minWidth: 0,
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  })
  const classes = useStyle();

  //
  // ─── REQUEST TO GET ALL NECESSARY DATA ───────────────────────────────────────
  //
  useEffect(() => {
    const fetchData = () => {
      Promise.all([
        Api({ endpoint: '/brands', query: { fields: ['name', 'brand_id'], orderBy: 'name', brands_to_exclude: ["a7a5d846-d8db-4533-a6f4-3e71735c1f8d", "51914131-4943-49b3-955f-53738211fe29"] }, method: 'GET' }),
        Api({ endpoint: '/analytics/loyalty', method: 'GET' }),
        Api({ endpoint: '/analytics', query: { fields: ['analytics.time_create', 'COALESCE(analytics.label, "") as label', 'analytics.gain_id', 'analytics.status', 'analytics.loyalty', 'COALESCE(brands.name, "") as brandName', 'COALESCE(stores.name, "") as storeName', 'COALESCE(users.name, "") as userName', 'COALESCE(users.firstname, "") as userFirstname'], join_query: 'LEFT JOIN brands ON brands.brand_id=analytics.brand_id LEFT JOIN stores ON stores.store_id=analytics.store_id LEFT JOIN users on users.user_id=analytics.user_id', loyalty: 0, status: 1 }, method: 'GET' }),
        Api({ endpoint: '/analytics/users_points', method: 'GET' }),
        Api({ endpoint: '/users', query: { fields: ['COUNT(users.user_id) as utilisateurs'], status: 2, role: 2, removeAnalytics: true }, method: 'GET' }),
        Api({ endpoint: '/tracking_actu_offer', method: 'GET' }),
        Api({ endpoint: '/offers', query: { fields: ['title', 'offer_id'] }, method: 'GET' }),
        Api({ endpoint: '/actualities', query: { fields: ['title', 'actuality_id'] }, method: 'GET' }),
      ])
        .then(([brandReq, loyaltyReq, comptaReq, userPointsReq, usersReq, trackingReq, offersReq, actualitiesReq]) => {
          if (brandReq.success) setBrands(brandReq.data);
          if (loyaltyReq.success) setGainsPoints(loyaltyReq.data);
          if (comptaReq.success) setGainsCompta(comptaReq.data);
          if (userPointsReq.success) setUserPoints(userPointsReq.data);
          if (usersReq.success) setAnalyticsUsers(usersReq.data);
          if (trackingReq.success) setTrackingActuOffer(trackingReq.data);
          if (offersReq.success) setOffers(offersReq.data);
          if (actualitiesReq.success) setActualities(actualitiesReq.data);
          setLoading(false);
        });
    }

    setBrands([]);
    setGainsPoints([]);
    setGainsCompta([]);
    setUserPoints([]);
    setAnalyticsUsers([]);
    setTrackingActuOffer([]);
    fetchData();
  }, []);

  const trackingActualities = trackingActuOffer.filter(tracking => tracking.type === 1) ?? [];
  const trackingOffers = trackingActuOffer.filter(tracking => tracking.type === 2) ?? [];
  const actualitiesWithViews = actualities.map(actuality => {
    let views = 0;
    let percentage = 0;
    const tracked = trackingActualities.find(trackedItem => {
      return trackedItem.type_id === actuality.actuality_id;
    });
    if (tracked) { views = tracked.views; percentage = tracked.views / analyticsUsers[0].utilisateurs * 100; }
    return {
      ...actuality,
      views,
      percentage
    };
  });
  const offersWithViews = offers.map(offer => {
    let views = 0;
    let percentage = 0;
    const tracked = trackingOffers.find(trackedItem => {
      return trackedItem.type_id === offer.offer_id;
    });
    if (tracked) { views = tracked.views; percentage = tracked.views / analyticsUsers[0].utilisateurs * 100; }
    return {
      ...offer,
      views,
      percentage
    };
  });

  // 
  // ─── COMPONENT RENDER ───────────────────────────────────────
  //
  const emptyState = <div className='text'>Pas de données d'analytics pour le moment.</div>

  const CardHeader = ({ title, subtitle }) => <div className={classes.cardHeader}>
    <Typography variant="h2" component="h2" className={classes.cardTitle}>{title}</Typography>
    {subtitle && <div className='text'>{subtitle}</div>}
  </div>

  if (loading || !analyticsUsers.length) return null;

  return <div className={classes.page}>
    <Typography variant="h1" component="h1">Statistiques</Typography>

    {/* ─── VUE D'ENSEMBLE ─── */}
    <div className={userData.role === 1 ? classes.topRow : undefined}>
      <div className={`${classes.card} ${classes.kpi}`}>
        <CardHeader title="Inscrits" subtitle="Nombre d’inscrits au programme de fidélité" />
        <span className='number'>{analyticsUsers[0].utilisateurs}</span>
      </div>
      {userData.role === 1 && <div className={classes.card}>
        <CardHeader title="Enseignes" subtitle="Cliquer sur une enseigne pour voir son détail" />
        <div className={classes.brandContainer}>
          {brands.map(brand => {
            return <Link className={classes.brandLink} to={`/admin/analytics/${brand.brand_id}`} key={brand.brand_id} title={brand.name}>
              <img src={`${process.env.REACT_APP_URL}/img/brands/${brand.brand_id}/${brand.logo}?${timeStamp()}`} alt={brand.name} />
            </Link>
          })}
        </div>
      </div>}
    </div>

    {/* ─── FREQUENTATION ─── */}
    <div className={classes.sectionTitle}>Fréquentation</div>
    <div className={classes.card}>
      <CardHeader title="Inscrits par enseigne" />
      <UsersBar />
    </div>
    <div className={classes.card}>
      <CardHeader title="Passage en caisse" />
      <BarAdmin brands={brands} />
    </div>

    {/* ─── CONTENUS ─── */}
    {userData.role === 1 && <>
      <div className={classes.sectionTitle}>Contenus</div>
      <div className={classes.grid2}>
        <div className={classes.card}>
          <CardHeader title="Actualités" />
          {trackingActualities.length ? <BlogTable type_title={'Actualité'} tracking={actualitiesWithViews} /> : emptyState}
        </div>
        <div className={classes.card}>
          <CardHeader title="Offres" />
          {trackingOffers.length ? <BlogTable type_title={'Offre'} tracking={offersWithViews} /> : emptyState}
        </div>
      </div>
    </>}

    {/* ─── PALIERS & GAINS ─── */}
    <div className={classes.sectionTitle}>Paliers & gains</div>
    <div className={classes.card}>
      <CardHeader title="Utilisation des paliers" />
      <div className={classes.doughnuts}>
        <div className={classes.doughnutCol}>
          <div className='text'>Points de fidélité des utilisateurs</div>
          {userPoints.length ? <DoughnutAdmin data={userPoints} /> : emptyState}
        </div>
        <div className={classes.doughnutCol}>
          <div className='text'>Gains paliers utilisés</div>
          {gainsPoints.length ? <DoughnutAdmin data={gainsPoints} /> : emptyState}
        </div>
      </div>
      <div className={classes.cardFooter}>
        <ExportServer
          endpoint='/export/palier'
          fileName='export_paliers_fidelite'
          label='Export XLSX'
        />
      </div>
    </div>
    {[1, 5].includes(userData.role) && <div className={classes.card}>
      <CardHeader title="Gains" />
      {gainsCompta.length ? <AnalyticsTable analytics={gainsCompta} name={true} /> : emptyState}
    </div>}
    {[1, 6].includes(userData.role) && <div className={classes.card}>
      <CardHeader title="Gains utilisés" />
      <MarketingAnalyticsTable brands={brands} />
    </div>}
  </div>
};

export default ListAnalytics;